<template>
    <div class="date-selector">
        <div class="date-selector__quick-buttons">
            <button class="button button--outline"
                :class="{ 'button--disabled': !isDateAllowed(DateService.getToday()) }"
                v-if="quickButtonToday"
                v-on:click="date = DateService.getToday()">
                {{$t('date_and_pax_stage.today')}}
            </button>
            <button class="button button--outline"
                :class="{ 'button--disabled': !isDateAllowed(DateService.getTomorrow()) }"
                v-if="quickButtonTomorrow"
                v-on:click="date = DateService.getTomorrow()">
                {{$t('date_and_pax_stage.tomorrow')}}
            </button>
        </div>

        <div v-show="loading" class="calendar__loading"></div>

        <div class="date-selector__next-available" v-if="shouldShowNextAvailable">
          <p v-if="nextAvailable.message">
            {{ nextAvailable.message }}
          </p>

          <p>
            {{$t('date_and_pax_stage.next_available_date')}}<strong>{{ DateService.friendlyDate(nextAvailable.nextAvailableDate) }}</strong>
          </p>

          <button class="button" v-on:click="jumpToNextAvailable">
            {{$t('date_and_pax_stage.jump_to_next_available')}}
          </button>
        </div>

        <calendar 
            v-on:selectedDisabled="showDateUnavailableMessage"
            v-on:changedMonth="changedMonth"
            v-model="date"
            maximum-view="day"
            ref="calendar"
            :monday-first="mondayFirst"
            :inline="true"
            :disabled="this.calendarDisabled"
            :language="this.locale"
            :highlighted="_getHighlightedDatesForCalendar()">
          </calendar>

    </div>
</template>

<script>
  import calendar from 'vuejs-datepicker'
  import DateService from '../services/DateService'
  import PromptService from '../services/PromptService'
  import DateAvailabilityProvider from '../services/DateAvailabilityProvider'
  import Venue from '../models/Venue'

  export default {
    name: 'dateSelector',
    props: {
      quickButtonToday: {
        type: Boolean
      },
      quickButtonTomorrow: {
        type: Boolean
      },
      mondayFirst: {
        type: Boolean
      },
      overrideDisabledDates: {
        type: Array
      },
      value: {
        type: Date
      },
      highlightedDates: {
        type: Array
      },
      venue: {
        type: Venue
      }
    },
    components: {
      calendar
    },
    data () {
      let locale = this.$root.dmnOptions.locale

      // If the locale is en-GB, just use en for the cal
      if (this.$root.dmnOptions.locale === 'en-GB') {
        locale = 'en'
      }

      return {
        DateService,
        date: this._getDefaultDate(),
        calendarDisabled: {},
        quickButtonsDisabled: {},
        loading: true,
        nextAvailable: null,
        dontShowNextAvailable: false,
        locale: locale
      }
    },
    methods: {
      _getDisabledDatesForCalendar () {
        return {
          to: DateService.getYesterday(),
          customPredictor: (date) => {
            return !this.isDateAllowed(date)
          }
        }
      },

      _getHighlightedDatesForCalendar () {
        return {
          dates: this.highlightedDates
        }
      },

      jumpToNextAvailable () {
        const date = this.nextAvailable.nextAvailableDate

        this.moveToMonth(date)
        this.nextAvailable = null
        this.dontShowNextAvailable = true
      },

      showDateUnavailableMessage (date) {
        // Get the unavailable message for this date.
        if (!(date instanceof Date)) {
          date = new Date(date.timestamp)
        }

        const availability = this.dateAvailabilityProvider.findAvailabilityForDate(
          date
        )

        // Error message
        const message = availability.errors[0].message
        const prompt = PromptService.make()
        prompt.title = message

        this.promptService.showNewPrompt(prompt)
        this.$root.prompt = this.promptService.getActivePrompt()
      },

      _getDefaultDate () {
        // has one already been set?
        if (this.value) {
          return this.value
        }

        return null
      },
      moveToMonth (date) {
        this.$refs.calendar.setPageDate(date)
        this.changedMonth(date)
      },
      isDateAllowed (date) {
        if (this.dateAvailabilityProvider.hasAvailabilityForMonth(date)) {
          return this.dateAvailabilityProvider.isDateAllowed(date)
        }

        return false
      },

      getAvailabilityForMonth (month) {
        return this.dateAvailabilityProvider.checkAvailabilityForMonth(month)
          .then((availability) => {
            this.rerenderDisabled()
            this.persistCache()
          })
      },

      changedMonth (date) {
        this.rerenderDisabled()

        if (!this.dateAvailabilityProvider.hasAvailabilityForMonth(date)) {
          this.loading = true
          this.getAvailabilityForMonth(date)
            .then(() => {
              this.loading = false
            })
        }

        const nextMonth = DateService.getNextMonth()

        if (!this.dateAvailabilityProvider.hasAvailabilityForMonth(nextMonth)) {
          this.getAvailabilityForMonth(nextMonth)
            .then(() => {
              this.loading = false
            })
        }
      },
      persistCache () {
        // Grab the cache from this.dateAvailabilityProvider
        // store it in this.$root.dateAvailabilityCache
        const cache = this.dateAvailabilityProvider.cache

        this.$root.dateAvailabilityCache = cache
      },
      rerenderDisabled () {
        this.calendarDisabled = {}
        this.calendarDisabled = this._getDisabledDatesForCalendar()
      },
      requestDateAvailability () {
        this.loading = true

        this.getNextAvailableDate()

        Promise.all([
          this.getAvailabilityForMonth(this._getDefaultDate() || new Date()),
          this.getAvailabilityForMonth(DateService.getNextMonth())
        ]).then(() => {
          this.loading = false
          this.persistCache()
          this.calendarDisabled = this._getDisabledDatesForCalendar()
        })
      },
      getNextAvailableDate () {
        this.dontShowNextAvailable = false
        this.nextAvailable = null

        this.dateAvailabilityProvider.checkNextAvailableDate()
          .then((unavailableDate) => {
            if (unavailableDate !== null && unavailableDate.isMoreThanOneMonthInFuture()) {
              this.nextAvailable = unavailableDate
            }
          })
      },
      setDate (date) {
        this.date = date
      }
    },
    created () {
      this.dateAvailabilityProvider = new DateAvailabilityProvider(
        this.$root.booking,
        this.$root.dateAvailabilityCache,
      )

      this.dateAvailabilityProvider.setUrlOptions({
        source: this.$root.dmnOptions.from,
        affiliateId: this.$root.dmnOptions.affiliateId,
        customSource: this.$root.dmnOptions.customSource
      })

      this.promptService = new PromptService(this.$root.prompt)
      this.calendarDisabled = this._getDisabledDatesForCalendar()
    },
    mounted () {
    },
    watch: {
      date: function (date) {
        if (this.isDateAllowed(date)) {
          this.$emit('input', date)
        } else {
          this.showDateUnavailableMessage(date)
        }
      },
      venue: function (venue) {
        this.dateAvailabilityProvider.resetCache()

        if (venue !== null) {
          this.moveToMonth(new Date())
        }
      }
    },
    computed: {
      shouldShowNextAvailable: function () {
        if (this.dontShowNextAvailable) {
          return false
        }

        if (this.nextAvailable !== null) {
          const currentMonth = this.$refs.calendar.pageDate

          return !DateService.isSameMonth(
            this.nextAvailable.nextAvailableDate,
            currentMonth
          )
        }

        return false
      }
    }
  }
</script>



// WEBPACK FOOTER //
// dateSelector.vue?4394f777